import { useState } from "react";
import axios from "axios";
import { X, SlidersHorizontal, RotateCcw, MapPin, Sparkles, Loader2, MessageSquare } from "lucide-react";
import { API, useAuth } from "@/App";
import { getLeadScore } from "@/lib/getLeadScore";

const LEAD_SOURCES = [
  { value: "sales_agent_referral", label: "Sales Agent Referral" },
  { value: "website", label: "Website" },
  { value: "gartner", label: "Gartner" },
  { value: "software_finder", label: "Software Finder" },
  { value: "ampliz", label: "Ampliz" },
  { value: "youtube", label: "Youtube" },
  { value: "facebook", label: "Facebook" },
  { value: "internal", label: "Internal" },
  { value: "other", label: "Other" },
];

const SPECIALTIES = [
  "Family Medicine", "Internal Medicine", "Pediatrics", "Geriatrics",
  "Cardiology", "Neurology", "Orthopedics", "Psychiatry", "Oncology",
  "OB/GYN", "Urgent Care", "Dermatology", "ENT", "Pain Management",
  "Behavioral Health", "Primary Care", "Multi-specialty", "Other",
];

const PRODUCTS = [
  "AI-powered EHR (MDNet)", "Practice Management", "Medical Billing / RCM",
  "RevQ AI Billing", "CCM", "RPM", "APCM", "AWV Program",
  "REACH / Patient Engagement", "Telemedicine", "EPCS", "AI Scribe Only",
];

const BUDGETS = [
  "Below $200", "$200-$300", "$300-$400", "$400-$500",
  "$500-$600", "Above $600", "Open/Flexible", "Price Sensitive", "High Budget",
];

const TIMELINES = ["ASAP (<30d)", "1-2 months", "2-4 months", "5-6 months", "7+ months"];

const CURRENT_EHRS = [
  "Epic", "athenahealth", "NextGen", "eClinicalWorks",
  "Allscripts", "Kareo", "Legacy/Outdated", "None/Paper", "Other",
];

const RATINGS = ["Hot", "Warm", "Cold", "Dormant"];

const PRACTICE_TYPES = ["New Practice", "Existing Practice", "Hybrid"];

// ── Ampliz/CMS-specific filter dimensions (Feb 2026 exec QA) ────────
// Exposed on both Leads and Sales Dialer filter panels so reps can
// segment Ampliz-enriched practices by capability gaps (red badges
// = sales opportunity) and operational signals.
const SIGNAL_TRI_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unknown", label: "Unknown" },
];
const BENES_BUCKETS = [
  { value: "0-50",       label: "Under 50",     min: 0,    max: 49 },
  { value: "50-200",     label: "50 – 200",     min: 50,   max: 199 },
  { value: "200-500",    label: "200 – 500",    min: 200,  max: 499 },
  { value: "500-1000",   label: "500 – 1,000",  min: 500,  max: 999 },
  { value: "1000+",      label: "1,000+",       min: 1000, max: Infinity },
];
const EHR_STAGES = ["Stage 1", "Stage 2", "Stage 3"];
const BILLING_STATUSES = ["In-House", "Outside"];
const INSURANCE_GROUPS = [
  { value: "bcbs",        label: "Blue Cross / Blue Shield" },
  { value: "aetna",       label: "Aetna" },
  { value: "cigna",       label: "Cigna" },
  { value: "humana",      label: "Humana" },
  { value: "united",      label: "United Healthcare" },
  { value: "anthem",      label: "Anthem" },
  { value: "medicare",    label: "Medicare" },
  { value: "medicaid",    label: "Medicaid" },
  { value: "tricare",     label: "Tricare / VA" },
  { value: "other",       label: "Other / Regional" },
];

const US_REGIONS = [
  { value: "northeast", label: "Northeast", states: ["CT", "DE", "ME", "MD", "MA", "NH", "NJ", "NY", "PA", "RI", "VT", "DC"] },
  { value: "southeast", label: "Southeast", states: ["AL", "AR", "FL", "GA", "KY", "LA", "MS", "NC", "SC", "TN", "VA", "WV"] },
  { value: "midwest", label: "Midwest", states: ["IL", "IN", "IA", "KS", "MI", "MN", "MO", "NE", "ND", "OH", "SD", "WI"] },
  { value: "southwest", label: "Southwest", states: ["AZ", "NM", "OK", "TX"] },
  { value: "west", label: "West", states: ["AK", "CA", "CO", "HI", "ID", "MT", "NV", "OR", "UT", "WA", "WY"] },
  { value: "no_location", label: "No location listed", states: [] },
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN",
  "IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH",
  "NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT",
  "VT","VA","WA","WV","WI","WY",
];

/**
 * Map IANA / abbreviated timezone strings to a US region bucket.
 * Pilot CSV imports populate `timezone` with values like "EST",
 * "Eastern Time", "America/New_York", etc. Returns one of the
 * `US_REGIONS` value slugs or `"no_location"`.
 */
export function regionFromTimezone(tz) {
  if (!tz) return null;
  const s = String(tz).toLowerCase().trim();
  if (!s) return null;
  // Eastern → northeast (default; the southeastern bucket is geographic
  // and overlaps the same offset). The CSV almost always means "Eastern
  // Time" rather than a state-level distinction here.
  if (/(^|[^a-z])(est|edt|eastern)($|[^a-z])/.test(s)
      || /america\/(new_york|detroit|indiana|kentucky|toronto)/.test(s)) {
    return "northeast";
  }
  if (/(^|[^a-z])(cst|cdt|central)($|[^a-z])/.test(s)
      || /america\/(chicago|menominee|knox|tell_city|winnipeg)/.test(s)) {
    return "midwest";
  }
  if (/(^|[^a-z])(mst|mdt|mountain)($|[^a-z])/.test(s)
      || /america\/(denver|phoenix|boise|edmonton|salt_lake)/.test(s)) {
    // Mountain spans Southwest (AZ/NM) + West (CO/UT/MT/ID/WY). Without
    // state-level data we bucket as `west` — it's the dominant tz user.
    return "west";
  }
  if (/(^|[^a-z])(pst|pdt|pacific)($|[^a-z])/.test(s)
      || /america\/(los_angeles|tijuana|vancouver|seattle)/.test(s)) {
    return "west";
  }
  if (/(^|[^a-z])(akst|akdt|alaska|hst|hdt|hawaii|chst|sst)($|[^a-z])/.test(s)
      || /america\/anchorage|pacific\/honolulu|pacific\/guam/.test(s)) {
    return "west";
  }
  return null;
}

/**
 * Resolve a lead's region for filter purposes. Order of preference:
 *   1) Explicit `lead.region` if the importer set it (some templates do).
 *   2) `regionFromTimezone(lead.timezone)` — the populated field today.
 *   3) `getRegionForState(lead.state)` — legacy fallback.
 *   4) `"no_location"`.
 */
export function getRegionForLead(lead) {
  if (!lead) return "no_location";
  if (lead.region) {
    const r = String(lead.region).toLowerCase().trim();
    if (US_REGIONS.some(x => x.value === r)) return r;
  }
  const fromTz = regionFromTimezone(lead.timezone);
  if (fromTz) return fromTz;
  if (lead.state) return getRegionForState(lead.state);
  return "no_location";
}

export function getRegionForState(stateCode) {
  if (!stateCode) return "no_location";
  const upper = stateCode.toUpperCase().trim();
  for (const region of US_REGIONS) {
    if (region.states.includes(upper)) return region.value;
  }
  return "no_location";
}

function FilterSection({ label, options, selected, onToggle, testPrefix }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</label>
        {selected.length > 0 && <span style={{ fontSize: 9, color: "var(--blue)", fontWeight: 600 }}>{selected.length}</span>}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {options.map(opt => {
          const val = typeof opt === "object" ? opt.value : opt;
          const lbl = typeof opt === "object" ? opt.label : opt;
          const isSelected = selected.includes(val);
          return (
            <button key={val} onClick={() => onToggle(val)}
              data-testid={`filter-${testPrefix}-${val.replace(/[\s\/()$+]+/g, '-').toLowerCase()}`}
              style={{
                padding: "4px 10px", fontSize: 11, borderRadius: 6,
                border: `0.5px solid ${isSelected ? "var(--blue)" : "var(--border)"}`,
                background: isSelected ? "var(--blue-light)" : "transparent",
                color: isSelected ? "var(--blue)" : "var(--text-secondary)",
                cursor: "pointer", fontWeight: isSelected ? 600 : 400,
                fontFamily: "var(--font)", transition: "all 0.15s",
              }}>
              {lbl}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const EMPTY_LEAD_FILTERS = {
  sources: [], specialties: [], products: [], budgets: [],
  timelines: [], ehrs: [], ratings: [], practiceTypes: [], regions: [],
  // Ampliz/CMS dimensions (Feb 2026):
  acoParticipation: [],   // ["yes","no","unknown"]
  medicareAssignment: [],
  telehealth: [],
  erx: [],
  pqrs: [],
  boardCert: [],
  benesBuckets: [],       // bucket .value strings
  ehrStages: [],
  insuranceGroups: [],
  billingStatuses: [],
  hasEhrData: [],         // ["yes","no"]
  states: [],             // 2-letter state codes
};

export function countActiveFilters(filters) {
  return Object.values(filters).reduce((sum, arr) => sum + arr.length, 0);
}

export function applyLeadFilters(leads, filters) {
  return leads.filter(lead => {
    if (filters.sources.length > 0) {
      const src = (lead.lead_source || "").toLowerCase().replace(/\s+/g, "_");
      if (!filters.sources.some(f => src === f || src.includes(f.replace(/\s+/g, "_")))) return false;
    }
    if (filters.specialties.length > 0) {
      // Iter 175.2 (Cluster A — Stephanie's `d205d47f` + himagna's
      // `d6d6887d`): the specialty filter previously returned 0 results
      // for Family Medicine / Primary Care / Multi-specialty even when
      // many leads were a perfect match. Root cause (per the iter-166
      // comment in `lib/leadDisplay.js`): Ampliz's Phase-2 import stamped
      // `lead.specialty = "Internal Medicine"` on every single row,
      // drowning the real signal. The DISPLAY layer already fixed this
      // by preferring `enrichment.nppes.primary_specialty` — but the
      // FILTER layer was still asking the lying `specialty` scalar.
      //
      // Fix: split the haystack into AUTHORITATIVE (NPPES + multi-
      // select) and LEGACY (the scalar that Ampliz vandalized). For
      // umbrella categories ("Primary Care", "OB/GYN") we only match
      // against the authoritative haystack, because "Primary Care"
      // matched against the lying "Internal Medicine" scalar would
      // return every single Ampliz lead. For literal specialty names
      // we still consult the legacy scalar so back-compat is preserved.
      const PRIMARY_CARE_SYNONYMS = [
        "family medicine", "internal medicine", "pediatrics",
        "geriatrics", "general practice", "family practice",
        "primary care",
      ];
      const OBGYN_SYNONYMS = [
        "ob/gyn", "obgyn", "obstetrics & gynecology",
        "obstetrics and gynecology", "obstetrics/gynecology",
        "ob gyn", "obstetrics", "gynecology",
      ];
      // Returns {needles, authoritativeOnly} — `authoritativeOnly`
      // forces the matcher to ignore the lying legacy scalar.
      const expandFilter = (f) => {
        const n = String(f || "").toLowerCase();
        if (n === "primary care") return { needles: PRIMARY_CARE_SYNONYMS, authoritativeOnly: true };
        if (n === "ob/gyn" || n === "obgyn") return { needles: OBGYN_SYNONYMS, authoritativeOnly: true };
        return { needles: [n], authoritativeOnly: false };
      };
      // Authoritative haystack — NPPES truth + multi-select.
      const authoritative = [
        lead.enrichment?.nppes?.primary_specialty || "",
        lead.nppes?.specialty || "",
        lead.nppes?.primary_specialty || "",
        ...(Array.isArray(lead.enrichment?.nppes?.specialties)
          ? lead.enrichment.nppes.specialties.flatMap(s =>
              typeof s === "string" ? [s] : [s?.name, s?.taxonomy_description])
          : []),
        ...(Array.isArray(lead.specialty_multi) ? lead.specialty_multi : []),
      ].map(s => String(s || "").toLowerCase()).filter(Boolean);
      // Legacy haystack — the scalar that Ampliz vandalized + contact-
      // level specialty. Only consulted for literal filter values.
      const legacy = [
        lead.specialty || "",
        ...((Array.isArray(lead.contacts) ? lead.contacts : []).flatMap(c => [c?.specialty, c?.specialty_group]).filter(Boolean)),
      ].map(s => String(s || "").toLowerCase()).filter(Boolean);
      const matched = filters.specialties.some(f => {
        const { needles, authoritativeOnly } = expandFilter(f);
        const hay = authoritativeOnly ? authoritative : [...authoritative, ...legacy];
        return needles.some(needle =>
          hay.some(h => h.includes(needle) || needle.includes(h)));
      });
      if (!matched) return false;
    }
    if (filters.products.length > 0) {
      // Live import populates `product_type` (multi-array), the legacy
      // dev seed uses `product_interest`. Combine both into a haystack
      // and substring-match — same pattern as Specialty.
      const haystack = [
        ...(Array.isArray(lead.product_type) ? lead.product_type : []),
        ...(Array.isArray(lead.product_interest) ? lead.product_interest : []),
      ].map(p => String(p || "").toLowerCase()).filter(Boolean);
      const matched = filters.products.some(f => {
        const needle = String(f || "").toLowerCase();
        return haystack.some(h => h.includes(needle) || needle.includes(h));
      });
      if (!matched) return false;
    }
    if (filters.budgets.length > 0) {
      // Whitespace + case-insensitive match — CSV variants like
      // "$200-$300" / "$200 - $300" / "200-300" all bucket as the same.
      const norm = s => String(s || "").toLowerCase().replace(/[\s$]/g, "");
      const v = norm(lead.budget_range);
      if (!filters.budgets.some(f => norm(f) === v)) return false;
    }
    if (filters.timelines.length > 0) {
      // Pilot CSV populates `implementation_timeline` (canonical Phase-2
      // mapping). Legacy seed used `decision_timeline`. Try both, normalize
      // for substring match because the CSV values like "1 to 2 months"
      // use different separators than the UI labels "1-2 months".
      const norm = s => String(s || "").toLowerCase().replace(/[\s_-]+/g, "").replace(/to/g, "");
      const v = norm(lead.implementation_timeline || lead.decision_timeline);
      if (!filters.timelines.some(f => {
        const fNorm = norm(f);
        return fNorm === v || fNorm.includes(v) || v.includes(fNorm);
      })) return false;
    }
    if (filters.ehrs.length > 0) {
      // Source-of-truth fields, in priority order:
      //   1) `current_ehr` / `current_ehrs` (legacy import)
      //   2) `ehr.vendor` (Ampliz / CHPL enrichment) — without this
      //      every Ampliz+EHR filter combo returned 0 results, even
      //      though 65% of Ampliz leads carry an EHR vendor.
      //   3) `ehr.product_name` — catches cases where the vendor
      //      column is empty but the product is (e.g. "eClinicalTouch"
      //      where vendor is blank).
      const haystack = [
        lead.current_ehr || "",
        lead.current_ehrs || "",
        lead.ehr?.vendor || "",
        lead.ehr?.product_name || "",
      ].map(s => String(s || "").toLowerCase()).filter(Boolean);
      const matched = filters.ehrs.some(f => {
        const needle = String(f || "").toLowerCase();
        return haystack.some(h => h.includes(needle) || needle.includes(h));
      });
      if (!matched) return false;
    }
    if (filters.ratings.length > 0) {
      // Prefer the imported canonical `lead_rating` (Hot/Warm/Cold/
      // Dormant). Pre-fix this branch ONLY derived from `ai_score`,
      // which is null on most imported leads — so every CSV-imported
      // lead bucketed as "Dormant" regardless of the actual rating.
      let rating = String(lead.lead_rating || "").trim();
      if (!rating) {
        const score = getLeadScore(lead);
        rating = score >= 71 ? "Hot" : score >= 41 ? "Warm" : score > 0 ? "Cold" : "Dormant";
      }
      // Case-insensitive equality so "hot" / "HOT" / "Hot" all match.
      const r = rating.toLowerCase();
      if (!filters.ratings.some(f => String(f).toLowerCase() === r)) return false;
    }
    if (filters.practiceTypes.length > 0) {
      // Filter UI uses "New Practice" / "Existing Practice" / "Hybrid".
      // CSV canonical enum is "New" / "Existing" / "Hybrid" (per
      // ENUM_ALIASES in lead_import_validation.py). Substring match
      // both directions so "New Practice" filter hits "New" canonical.
      const v = String(lead.practice_type || "").toLowerCase();
      if (!v) return false;
      if (!filters.practiceTypes.some(f => {
        const fl = String(f).toLowerCase();
        return v === fl || v.includes(fl) || fl.includes(v);
      })) return false;
    }
    if (filters.regions.length > 0) {
      // Region resolves from explicit lead.region → timezone → state
      // → no_location. See `getRegionForLead` for the priority chain.
      const leadRegion = getRegionForLead(lead);
      if (!filters.regions.includes(leadRegion)) return false;
    }

    // ── Ampliz/CMS tri-state signal filters (Feb 2026 exec QA) ─────
    // Each tri-state filter accepts ["yes","no","unknown"] tokens and
    // matches against a strict boolean reading of the source field.
    const matchTri = (tokens, val) => {
      if (!tokens || tokens.length === 0) return true;
      const bucket = val === true ? "yes" : val === false ? "no" : "unknown";
      return tokens.includes(bucket);
    };
    if (!matchTri(filters.acoParticipation,   lead.aco_participation))        return false;
    if (!matchTri(filters.medicareAssignment, lead.signals?.medicare_assignment)) return false;
    if (!matchTri(filters.telehealth,         lead.signals?.telehealth))      return false;
    if (!matchTri(filters.erx,                lead.signals?.erx))             return false;
    if (!matchTri(filters.pqrs,               lead.signals?.pqrs))            return false;
    if (!matchTri(filters.boardCert,          lead.signals?.board_cert))      return false;

    if (filters.benesBuckets && filters.benesBuckets.length > 0) {
      const n = Number(lead.medicare?.beneficiaries);
      if (!Number.isFinite(n) || n < 0) return false;
      const hit = filters.benesBuckets.some(v => {
        const b = BENES_BUCKETS.find(x => x.value === v);
        return b && n >= b.min && n <= b.max;
      });
      if (!hit) return false;
    }
    if (filters.ehrStages && filters.ehrStages.length > 0) {
      const v = String(lead.ehr?.provider_stage_number || "").trim();
      if (!v || !filters.ehrStages.some(f => v.toLowerCase() === f.toLowerCase()
                                         || v.toLowerCase().includes(f.toLowerCase()))) return false;
    }
    if (filters.billingStatuses && filters.billingStatuses.length > 0) {
      const v = String(lead.billing_status || "").trim().toLowerCase();
      if (!v || !filters.billingStatuses.some(f => v === f.toLowerCase())) return false;
    }
    if (filters.insuranceGroups && filters.insuranceGroups.length > 0) {
      const v = String(lead.insurance_type || "").toLowerCase();
      if (!v) return false;
      const hit = filters.insuranceGroups.some(g => {
        if (g === "bcbs")     return v.includes("bcbs") || v.includes("blue cross") || v.includes("blueshield") || v.includes("blue shield");
        if (g === "aetna")    return v.includes("aetna");
        if (g === "cigna")    return v.includes("cigna");
        if (g === "humana")   return v.includes("humana");
        if (g === "united")   return v.includes("united");
        if (g === "anthem")   return v.includes("anthem");
        if (g === "medicare") return v.includes("medicare");
        if (g === "medicaid") return v.includes("medicaid");
        if (g === "tricare")  return v.includes("tricare") || v.includes("va ") || v.startsWith("va");
        if (g === "other") {
          return !["bcbs","blue cross","aetna","cigna","humana","united","anthem","medicare","medicaid","tricare"]
            .some(k => v.includes(k));
        }
        return false;
      });
      if (!hit) return false;
    }
    if (filters.hasEhrData && filters.hasEhrData.length > 0) {
      const has = !!(lead.ehr?.vendor || lead.ehr?.product_name);
      const bucket = has ? "yes" : "no";
      if (!filters.hasEhrData.includes(bucket)) return false;
    }
    if (filters.states && filters.states.length > 0) {
      const s = String(lead.state || "").toUpperCase();
      if (!filters.states.includes(s)) return false;
    }
    return true;
  });
}

export function formatLeadSource(src) {
  if (!src) return "Direct";
  return src.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

export const LEAD_SOURCES_LIST = LEAD_SOURCES;

export default function LeadFilterPanel({ filters, onChange, extra, onChangeExtra, onClose, onAiSearch, aiSearchActive }) {
  const safeExtra = extra || { state: [], city: [], zip: [] };
  const safeOnChangeExtra = onChangeExtra || (() => {});
  const activeCount = countActiveFilters(filters);
  const extraCount = (safeExtra.state.length + safeExtra.city.length + safeExtra.zip.length);

  const clearAll = () => {
    // Iter 242 — "Clear all" now clears the AI Search too, matching
    // the button's promise. Both modes reset together.
    onChange({ ...EMPTY_LEAD_FILTERS });
    safeOnChangeExtra({ state: [], city: [], zip: [] });
    if (aiSearchActive) onAiSearch?.(null);
  };

  return (
    <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 1000 }} data-testid="lead-filter-panel">
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.15)" }} onClick={onClose} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 380,
        background: "var(--surface)", borderLeft: "0.5px solid var(--border)",
        display: "flex", flexDirection: "column",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.08)",
        animation: "slideInRight 0.2s ease-out",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SlidersHorizontal style={{ width: 16, height: 16, color: "var(--blue)" }} />
            <span style={{ fontSize: 14, fontWeight: 650 }}>Filters</span>
            {(activeCount + extraCount) > 0 && <span className="tag tag-blue" style={{ fontSize: 9 }}>{activeCount + extraCount} active</span>}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {(activeCount + extraCount) > 0 && (
              <button className="btn" onClick={clearAll} style={{ fontSize: 10, color: "var(--red)" }} data-testid="clear-lead-filters-btn">
                <RotateCcw style={{ width: 11, height: 11 }} /> Clear all
              </button>
            )}
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="close-lead-filters-btn">
              <X style={{ width: 16, height: 16, color: "var(--text-tertiary)" }} />
            </button>
          </div>
        </div>
        <LeadFilterBody
          filters={filters}
          onChange={onChange}
          extra={safeExtra}
          onChangeExtra={safeOnChangeExtra}
          scrollPadding="16px 20px"
          onAiSearch={onAiSearch}
          aiSearchActive={aiSearchActive}
        />
      </div>
    </div>
  );
}

/**
 * LeadFilterBody — the scrollable filter content extracted from the
 * side-panel so other surfaces (e.g. New Campaign lightbox) can reuse
 * the exact same filter set without re-implementing it. Returns just
 * the inner content; caller wraps it in their own layout/overlay.
 */
export function LeadFilterBody({ filters, onChange, extra, onChangeExtra, scrollPadding = "16px 20px", maxHeight = undefined, showAiBuilder = true, onAiSearch, aiSearchActive }) {
  const { token } = useAuth();
  const safeExtra = extra || { state: [], city: [], zip: [] };
  const safeOnChangeExtra = onChangeExtra || (() => {});

  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiNotes, setAiNotes] = useState([]);
  const [aiResultMeta, setAiResultMeta] = useState(null); // { count, query, mongoQuery }
  const [cityDraft, setCityDraft] = useState("");
  const [zipDraft, setZipDraft] = useState("");

  const toggle = (field, value) => {
    const arr = filters[field] || [];
    onChange({ ...filters, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] });
  };

  const updateExtra = (key, list) => {
    safeOnChangeExtra({ ...safeExtra, [key]: list });
  };

  // ── 2026-06-29: "AI Search" replaces "Build a filter with AI" ──
  // The legacy parser mapped natural language ONTO this panel's limited
  // filter fields (state/city/zip/sources/specialties/…) which gave a
  // poor experience because most queries can't be expressed via those
  // fields. We now route directly through /leads/lumina/run (the same
  // engine that powers /lumina-search) — it returns the actual lead
  // matches and we surface those as a result-set the parent page
  // intersects with its other filters. No field-panel coupling.
  const runAiSearch = async () => {
    const text = aiPrompt.trim();
    if (!text || aiLoading) return;
    setAiLoading(true);
    setAiNotes([]);
    setAiResultMeta(null);
    try {
      const res = await axios.post(
        `${API}/leads/lumina/run`,
        { query: text, limit: 5000, skip: 0 },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const env = res.data || {};
      if (env.error) {
        setAiNotes([env.error]);
        return;
      }
      // Iter 238 (July 2026 bugfix) — backend returns matches under
      // `results` (from services/lumina_leads.execute_query). Previously
      // we read `env.leads` (which never existed), so total was correct
      // but ids was always [] → the /leads table showed nothing even
      // when the AI said "Matched N leads". Now read both keys defensively.
      const leads = Array.isArray(env.results)
        ? env.results
        : (Array.isArray(env.leads) ? env.leads : []);
      const ids = leads.map((l) => l.id).filter(Boolean);
      const total = env.total ?? ids.length;
      setAiResultMeta({
        count: total,
        returned: ids.length,
        query: env.natural_query || text,
        mongoQuery: env.mongo_query,
      });
      // Hand the result up — parent uses these ids to filter the table.
      onAiSearch?.({
        ids,
        total,
        query: env.natural_query || text,
        mongoQuery: env.mongo_query,
      });
      const notes = [];
      if (Array.isArray(env.normalization_notes)) notes.push(...env.normalization_notes);
      if (Array.isArray(env.warnings)) notes.push(...env.warnings);
      setAiNotes(notes);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't reach the AI service. Please try again.";
      setAiNotes([typeof msg === "string" ? msg : "AI search failed."]);
    } finally {
      setAiLoading(false);
    }
  };

  const clearAiSearch = () => {
    setAiPrompt("");
    setAiNotes([]);
    setAiResultMeta(null);
    onAiSearch?.(null);
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: scrollPadding, maxHeight }} data-testid="lead-filter-body">
      {showAiBuilder && (
          <div data-testid="ai-filter-section" style={{ marginBottom: 18, background: "#EEEDFE", border: "0.5px solid #CECBF6", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <Sparkles style={{ width: 13, height: 13, color: "#3C3489" }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: "#3C3489" }}>AI Search</span>
              {aiSearchActive && (
                <span data-testid="ai-search-active-pill" style={{
                  marginLeft: "auto", fontSize: 10, fontWeight: 600,
                  background: "#3C3489", color: "#fff",
                  padding: "2px 8px", borderRadius: 999,
                }}>
                  ACTIVE
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: "#5A4FA0", marginBottom: 8, lineHeight: 1.45 }}>
              Search ALL lead fields with natural language — same engine as <code>/lumina-search</code>.
              Results show in the table below and intersect with any specific filters you set.
            </div>
            <textarea
              data-testid="ai-filter-prompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); runAiSearch(); } }}
              placeholder='e.g. "cardiologists in TX who used Athena last year" or "leads contacted in last 7 days with no follow-up"'
              rows={3}
              style={{ width: "100%", border: "0.5px solid #CECBF6", borderRadius: 8, padding: "8px 10px", fontSize: 12, fontFamily: "inherit", resize: "vertical", outline: "none", background: "#fff", boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6, gap: 6 }}>
              {/* Clear AI search — always rendered for layout stability so
                  the three-button row doesn't shift between empty/active
                  states. Disabled when there's nothing to clear. */}
              <button
                data-testid="ai-search-clear-btn"
                onClick={clearAiSearch}
                disabled={!aiSearchActive && !aiResultMeta && !aiPrompt}
                title="Clear the AI search and reset to all leads"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  background: "#fff",
                  color: (!aiSearchActive && !aiResultMeta && !aiPrompt) ? "#B5AED9" : "#3C3489",
                  border: "0.5px solid #CECBF6", borderRadius: 8,
                  padding: "6px 12px", fontSize: 11, fontWeight: 500,
                  cursor: (!aiSearchActive && !aiResultMeta && !aiPrompt) ? "default" : "pointer",
                  fontFamily: "inherit",
                  opacity: (!aiSearchActive && !aiResultMeta && !aiPrompt) ? 0.55 : 1,
                }}
              >
                Clear AI search
              </button>
              <button
                data-testid="ai-filter-chat-btn"
                onClick={() => {
                  const prompt = (aiPrompt || "").trim();
                  if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("ai-chat:open-with-prompt", {
                      detail: { prompt: prompt || "Help me find the right leads. I'll describe what I need." },
                    }));
                  }
                }}
                title="Open AI chat to refine this query conversationally"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  background: "#fff", color: "#3C3489",
                  border: "0.5px solid #CECBF6", borderRadius: 8,
                  padding: "6px 12px", fontSize: 11, fontWeight: 500,
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                <MessageSquare style={{ width: 11, height: 11 }} />
                Chat
              </button>
              <button
                data-testid="ai-filter-parse-btn"
                onClick={runAiSearch}
                disabled={!aiPrompt.trim() || aiLoading}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  background: !aiPrompt.trim() || aiLoading ? "#A39CD8" : "#3C3489",
                  color: "#fff", border: "none", borderRadius: 8,
                  padding: "6px 14px", fontSize: 11, fontWeight: 500,
                  cursor: !aiPrompt.trim() || aiLoading ? "default" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                {aiLoading ? <Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> : <Sparkles style={{ width: 11, height: 11 }} />}
                {aiLoading ? "Searching…" : "Search with AI ✦"}
              </button>
            </div>
            {aiResultMeta && (
              <div
                data-testid="ai-search-result-meta"
                style={{
                  marginTop: 8, padding: "8px 10px", background: "#fff",
                  borderRadius: 6, border: "0.5px solid #C7BFEE",
                  fontSize: 11, color: "#3C3489",
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 2 }}>
                  ✦ Matched {aiResultMeta.count.toLocaleString()} lead{aiResultMeta.count === 1 ? "" : "s"}
                  {aiResultMeta.returned < aiResultMeta.count && (
                    <span style={{ marginLeft: 6, fontWeight: 500, opacity: 0.7 }}>
                      (showing first {aiResultMeta.returned.toLocaleString()})
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 10, color: "#5A4FA0", fontStyle: "italic", lineHeight: 1.4 }}>
                  &ldquo;{aiResultMeta.query}&rdquo;
                </div>
              </div>
            )}
            {aiNotes.length > 0 && (
              <div data-testid="ai-filter-notes" style={{ marginTop: 8, padding: "8px 10px", background: "#fff", borderRadius: 6, border: "0.5px solid #E0DBF8" }}>
                {aiNotes.map((n, i) => (
                  <div key={i} style={{ fontSize: 11, color: "#5A4FA0", lineHeight: 1.45, marginBottom: i === aiNotes.length - 1 ? 0 : 4 }}>• {n}</div>
                ))}
              </div>
            )}
          </div>
      )}

      {/* §3 Address tag filters — State / City / ZIP */}
      <div style={{ marginBottom: 18 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 6, display: "block" }}>State</label>
        <select
          data-testid="lead-filter-state-add"
          onChange={(e) => {
            const v = e.target.value;
            if (!v) return;
            if (!safeExtra.state.includes(v)) updateExtra("state", [...safeExtra.state, v]);
            e.target.value = "";
          }}
          style={{ fontSize: 12, padding: "6px 10px", border: "0.5px solid var(--border)", borderRadius: 6, fontFamily: "inherit", width: "100%" }}
        >
          <option value="">+ Add state…</option>
          {US_STATES.filter((s) => !safeExtra.state.includes(s)).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
          {safeExtra.state.map((s) => (
            <span key={s} data-testid={`lead-filter-state-pill-${s}`} style={{ background: "var(--blue-soft)", color: "var(--blue)", borderRadius: 999, padding: "2px 10px", fontSize: 11, display: "inline-flex", gap: 4 }}>
              {s}
              <button onClick={() => updateExtra("state", safeExtra.state.filter((x) => x !== s))} style={{ background: "none", border: "none", color: "var(--blue)", cursor: "pointer", padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 6, display: "block" }}>City</label>
        <input
          data-testid="lead-filter-city-input"
          value={cityDraft}
          onChange={(e) => setCityDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            const v = cityDraft.trim();
            if (!v) return;
            if (!safeExtra.city.includes(v) && safeExtra.city.length < 10) updateExtra("city", [...safeExtra.city, v]);
            setCityDraft("");
          }}
          placeholder="Press Enter to add (max 10)"
          style={{ fontSize: 12, padding: "6px 10px", border: "0.5px solid var(--border)", borderRadius: 6, fontFamily: "inherit", width: "100%", boxSizing: "border-box" }}
        />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
          {safeExtra.city.map((c) => (
            <span key={c} data-testid={`lead-filter-city-pill-${c}`} style={{ background: "var(--blue-soft)", color: "var(--blue)", borderRadius: 999, padding: "2px 10px", fontSize: 11, display: "inline-flex", gap: 4 }}>
              {c}
              <button onClick={() => updateExtra("city", safeExtra.city.filter((x) => x !== c))} style={{ background: "none", border: "none", color: "var(--blue)", cursor: "pointer", padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 6, display: "block" }}>ZIP code</label>
        <input
          data-testid="lead-filter-zip-input"
          value={zipDraft}
          onChange={(e) => setZipDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            const v = zipDraft.trim();
            if (!v) return;
            if (!safeExtra.zip.includes(v) && safeExtra.zip.length < 10) updateExtra("zip", [...safeExtra.zip, v]);
            setZipDraft("");
          }}
          placeholder="Press Enter to add (max 10)"
          style={{ fontSize: 12, padding: "6px 10px", border: "0.5px solid var(--border)", borderRadius: 6, fontFamily: "inherit", width: "100%", boxSizing: "border-box" }}
        />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
          {safeExtra.zip.map((z) => (
            <span key={z} data-testid={`lead-filter-zip-pill-${z}`} style={{ background: "var(--blue-soft)", color: "var(--blue)", borderRadius: 999, padding: "2px 10px", fontSize: 11, display: "inline-flex", gap: 4 }}>
              {z}
              <button onClick={() => updateExtra("zip", safeExtra.zip.filter((x) => x !== z))} style={{ background: "none", border: "none", color: "var(--blue)", cursor: "pointer", padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      </div>

      {/* Location / Region Filter */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.3px", display: "flex", alignItems: "center", gap: 4 }}>
            <MapPin style={{ width: 11, height: 11 }} /> Location / Region
          </label>
          {(filters.regions || []).length > 0 && <span style={{ fontSize: 9, color: "var(--blue)", fontWeight: 600 }}>{filters.regions.length}</span>}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {US_REGIONS.map(r => {
            const isSelected = (filters.regions || []).includes(r.value);
            return (
              <button key={r.value} onClick={() => toggle("regions", r.value)}
                data-testid={`filter-region-${r.value}`}
                style={{
                  padding: "4px 10px", fontSize: 11, borderRadius: 6,
                  border: `0.5px solid ${isSelected ? "var(--blue)" : "var(--border)"}`,
                  background: isSelected ? "var(--blue-light)" : "transparent",
                  color: isSelected ? "var(--blue)" : r.value === "no_location" ? "var(--text-tertiary)" : "var(--text-secondary)",
                  cursor: "pointer", fontWeight: isSelected ? 600 : 400,
                  fontFamily: "var(--font)", transition: "all 0.15s",
                  fontStyle: r.value === "no_location" ? "italic" : "normal",
                }}>
                {r.label}
                {r.states.length > 0 && <span style={{ fontSize: 9, color: "var(--text-tertiary)", marginLeft: 3 }}>({r.states.length})</span>}
              </button>
            );
          })}
        </div>
        {(filters.regions || []).length > 0 && (filters.regions || []).some(r => r !== "no_location") && (
          <div style={{ marginTop: 6, fontSize: 10, color: "var(--text-tertiary)", lineHeight: 1.5 }}>
            States: {US_REGIONS.filter(r => (filters.regions || []).includes(r.value) && r.states.length > 0).flatMap(r => r.states).join(", ")}
          </div>
        )}
      </div>
      <FilterSection label="Lead Source" options={LEAD_SOURCES} selected={filters.sources} onToggle={v => toggle("sources", v)} testPrefix="source" />
      <FilterSection label="Specialty" options={SPECIALTIES} selected={filters.specialties} onToggle={v => toggle("specialties", v)} testPrefix="spec" />
      <FilterSection label="Products of Interest" options={PRODUCTS} selected={filters.products} onToggle={v => toggle("products", v)} testPrefix="prod" />
      <FilterSection label="Budget Range" options={BUDGETS} selected={filters.budgets} onToggle={v => toggle("budgets", v)} testPrefix="budget" />
      <FilterSection label="Implementation Timeline" options={TIMELINES} selected={filters.timelines} onToggle={v => toggle("timelines", v)} testPrefix="timeline" />
      <FilterSection label="Current EHR" options={CURRENT_EHRS} selected={filters.ehrs} onToggle={v => toggle("ehrs", v)} testPrefix="ehr" />
      <FilterSection label="Lead Rating" options={RATINGS} selected={filters.ratings} onToggle={v => toggle("ratings", v)} testPrefix="rating" />
      <FilterSection label="Practice Type" options={PRACTICE_TYPES} selected={filters.practiceTypes} onToggle={v => toggle("practiceTypes", v)} testPrefix="ptype" />

      {/* ── Ampliz / CMS dimensions ──────────────────────────
          Surfaced as filters so reps can segment by capability
          gap (red = sales opportunity), participation, billing,
          and Medicare-volume bracket. */}
      <div style={{ margin: "20px 0 8px 0", padding: "8px 0", borderTop: "0.5px solid var(--border)" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          Ampliz / CMS dimensions
        </div>
      </div>
      <FilterSection label="ACO Participation"   options={SIGNAL_TRI_OPTIONS} selected={filters.acoParticipation}   onToggle={v => toggle("acoParticipation", v)}   testPrefix="aco" />
      <FilterSection label="Medicare Assignment" options={SIGNAL_TRI_OPTIONS} selected={filters.medicareAssignment} onToggle={v => toggle("medicareAssignment", v)} testPrefix="medicare" />
      <FilterSection label="Telehealth"          options={SIGNAL_TRI_OPTIONS} selected={filters.telehealth}         onToggle={v => toggle("telehealth", v)}         testPrefix="telehealth" />
      <FilterSection label="eRx"                 options={SIGNAL_TRI_OPTIONS} selected={filters.erx}                onToggle={v => toggle("erx", v)}                testPrefix="erx" />
      <FilterSection label="PQRS"                options={SIGNAL_TRI_OPTIONS} selected={filters.pqrs}               onToggle={v => toggle("pqrs", v)}               testPrefix="pqrs" />
      <FilterSection label="Board Certified"     options={SIGNAL_TRI_OPTIONS} selected={filters.boardCert}          onToggle={v => toggle("boardCert", v)}          testPrefix="boardcert" />
      <FilterSection label="Medicare Beneficiaries" options={BENES_BUCKETS}   selected={filters.benesBuckets}       onToggle={v => toggle("benesBuckets", v)}       testPrefix="benes" />
      <FilterSection label="EHR Stage (MIPS)"    options={EHR_STAGES}         selected={filters.ehrStages}          onToggle={v => toggle("ehrStages", v)}          testPrefix="ehrstage" />
      <FilterSection label="Billing Status"      options={BILLING_STATUSES}   selected={filters.billingStatuses}    onToggle={v => toggle("billingStatuses", v)}    testPrefix="billing" />
      <FilterSection label="Primary Insurance"   options={INSURANCE_GROUPS}   selected={filters.insuranceGroups}    onToggle={v => toggle("insuranceGroups", v)}    testPrefix="insurance" />
      <FilterSection label="Has EHR Data" options={[{ value: "yes", label: "EHR on file" }, { value: "no", label: "No EHR data" }]} selected={filters.hasEhrData} onToggle={v => toggle("hasEhrData", v)} testPrefix="hasehr" />
      <FilterSection label="State" options={US_STATES} selected={filters.states} onToggle={v => toggle("states", v)} testPrefix="state" />
    </div>
  );
}
